// frontend/src/utils/apiErrorUtils.js
import { getStoredUser } from "@utils/cookies";

export const DEV_ERROR_HANDLING_TEAM_CONTACTS = [
  "htran@smilesfirstcorp.com",
  "chui@smilesfirstcorp.com",
];

const DEV_TEAM_CONTACT_LINE = `Feel free to contact the development team: ${DEV_ERROR_HANDLING_TEAM_CONTACTS.join(", ")}`;

function formatDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const parts = detail.map((item) => {
      if (typeof item === "string") return item;
      if (item && typeof item === "object") {
        if (typeof item.message === "string") return item.message;
        if (typeof item.detail === "string") return item.detail;
      }
      return item != null ? String(item) : "";
    });
    const joined = parts.filter(Boolean).join(" ").trim();
    return joined || null;
  }
  return String(detail);
}

function looksLikeHtmlErrorPage(value) {
  if (typeof value !== "string") return false;
  const s = value.trim().toLowerCase();
  return (
    s.startsWith("<!doctype html") ||
    s.startsWith("<html") ||
    s.includes("<body") ||
    s.includes("django version") ||
    s.includes("traceback") ||
    s.includes("keyerror at ")
  );
}

function getServerIssueMessage() {
  return `Something went wrong on our side. Please try again later. ${DEV_TEAM_CONTACT_LINE}`;
}

export function isServerIssueError(error) {
  const status = error?.response?.status;
  const data = error?.response?.data;
  return status >= 500 || (typeof data === "string" && looksLikeHtmlErrorPage(data));
}

export function getSupportEmailHref(error) {
  if (!isServerIssueError(error)) return null;

  const user = getStoredUser();
  const userLabel =
    user?.full_name ||
    [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim() ||
    user?.email ||
    "Unknown";
  const message = getApiErrorMessage(error);
  const body = [
    `Time: ${new Date().toISOString()}`,
    `User: ${userLabel}`,
    `Error message: ${message}`,
  ].join("\n");

  return `mailto:${DEV_ERROR_HANDLING_TEAM_CONTACTS.join(";")}?subject=${encodeURIComponent("Smiles Analytics error report")}&body=${encodeURIComponent(body)}`;
}

/**
 * Normalizes API error responses into a single user-facing message.
 * Supports: detail, non_field_errors, array-based errors, details (nested),
 * and generic field-keyed objects.
 */
export function getApiErrorMessage(e) {
  const status = e?.response?.status;
  const data = e?.response?.data;

  if (!e?.response) {
    // Client-side validation errors (and any other thrown Error without an HTTP
    // response) carry a useful `.message` — surface it instead of pretending it
    // was a network failure.
    if (Array.isArray(e?.validationErrors) && e.validationErrors.length > 0) {
      return e.validationErrors.join("\n");
    }
    if (e instanceof Error && e.message && !/network error/i.test(e.message)) {
      return e.message;
    }
    return "Unable to reach the server. Please check your connection and try again.";
  }

  if (typeof data === "string" && data.trim()) {
    if (looksLikeHtmlErrorPage(data) || status >= 500) return getServerIssueMessage();
    return data;
  }

  if (Array.isArray(data) && data.length > 0) {
    const first = data[0];
    if (typeof first === "string") return first;
    const fromObj = first && typeof first === "object" ? formatDetail(first.detail) : null;
    if (fromObj) return fromObj;
    return String(first);
  }

  if (data && typeof data === "object") {
    const detailMsg = formatDetail(data.detail);
    if (detailMsg) return detailMsg;

    const nonField = data.non_field_errors;
    if (nonField != null) {
      if (Array.isArray(nonField) && nonField.length > 0) {
        return nonField
          .map((x) => String(x))
          .filter(Boolean)
          .join(" ");
      }
      return String(nonField);
    }

    if (typeof data.error === "string" && data.error.trim()) return data.error;

    const details = data.details;
    if (details && typeof details === "object" && !Array.isArray(details)) {
      const keys = Object.keys(details);
      if (keys.length > 0) {
        const firstVal = details[keys[0]];
        const msg = Array.isArray(firstVal) ? firstVal[0] : firstVal;
        if (msg != null) return String(msg);
      }
    }

    const keys = Object.keys(data);
    if (keys.length > 0) {
      const k = keys[0];
      const v = data[k];
      if (Array.isArray(v) && v[0] != null) return `${k}: ${v[0]}`;
      if (typeof v === "string") return `${k}: ${v}`;
    }
  }

  if (status >= 500) return getServerIssueMessage();
  if (status === 404) return "The requested resource could not be found.";
  if (status === 403) return "You do not have permission to perform this action.";
  if (status === 401) return "Your session has expired. Please log in again.";
  if (status === 400) return "Invalid request. Please check your input and try again.";

  return e?.message || "Request failed";
}
